import { useEffect, useState } from "react";
import {
  ApiRequestError,
  enableInteractionAssigneeImport,
  getSalesforceConfig,
  getSalesforceJwtCertificate,
  putSalesforceConfig,
  triggerBatchExport,
  triggerImport,
} from "../lib/client";
import {
  CONNECTION_FIELDS,
  CONSTITUENT_ASSIGNEES_EXPORT_RESOURCE,
  type ConfigFieldDef,
  IMPORTABLE_RESOURCES,
  INTERACTIONS_RESOURCE_NAME,
  RESOURCE_CONFIGURATION_SECTIONS,
  type ResourceConfigurationSectionDef,
  SETTINGS_FIELDS,
  SUBRESOURCE_DRY_RUN,
} from "../lib/salesforce-schema";
import type {
  EnableInteractionAssigneeImportInput,
  InteractionObjectAssigneeFieldInput,
  SalesforceConfigInput,
  SalesforceConfiguredSubresource,
  SalesforceResourceConfiguration,
} from "../lib/types";

/**
 * Normalize one subresource entry. Bare strings coerce to dry_run=true
 * (same as SalesforceConfiguredSubresource on the API).
 */
function normalizeConfiguredSubresource(
  value: unknown,
): SalesforceConfiguredSubresource | null {
  if (typeof value === "string" && value !== "") {
    return { subresource: value, dry_run: true };
  }
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const entry = value as Record<string, unknown>;
  const name = String(entry.subresource ?? "").trim();
  if (name === "") return null;
  return { subresource: name, dry_run: Boolean(entry.dry_run) };
}

function normalizeSubresourceList(
  value: unknown,
): SalesforceConfiguredSubresource[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: SalesforceConfiguredSubresource[] = [];
  for (const item of value) {
    const normalized = normalizeConfiguredSubresource(item);
    if (!normalized || seen.has(normalized.subresource)) continue;
    seen.add(normalized.subresource);
    out.push(normalized);
  }
  return out;
}

function normalizeResourceConfiguration(
  value: unknown,
): SalesforceResourceConfiguration[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (entry): entry is Record<string, unknown> =>
        entry != null && typeof entry === "object" && !Array.isArray(entry),
    )
    .map((entry) => ({
      resource_name: String(entry.resource_name ?? ""),
      flags: Array.isArray(entry.flags) ? entry.flags.map(String) : [],
      subresources_to_import: normalizeSubresourceList(
        entry.subresources_to_import,
      ),
      subresources_to_export: normalizeSubresourceList(
        entry.subresources_to_export,
      ),
    }))
    .filter((entry) => entry.resource_name !== "");
}

function getResourceEntry(
  configs: SalesforceResourceConfiguration[],
  resourceName: string,
): SalesforceResourceConfiguration {
  return (
    configs.find((e) => e.resource_name === resourceName) ?? {
      resource_name: resourceName,
      flags: [],
      subresources_to_import: [],
      subresources_to_export: [],
    }
  );
}

/** Upsert a resource_configuration entry; drop it when empty so we don't send noise. */
function withResourceEntry(
  configs: SalesforceResourceConfiguration[],
  resourceName: string,
  next: SalesforceResourceConfiguration,
): SalesforceResourceConfiguration[] {
  const others = configs.filter((e) => e.resource_name !== resourceName);
  const flags = next.flags ?? [];
  const subIn = next.subresources_to_import ?? [];
  const subOut = next.subresources_to_export ?? [];
  if (flags.length === 0 && subIn.length === 0 && subOut.length === 0) {
    return others;
  }
  return [
    {
      resource_name: resourceName,
      flags,
      subresources_to_import: subIn,
      subresources_to_export: subOut,
    },
    ...others,
  ];
}

function toggleListValue(
  values: string[],
  value: string,
  on: boolean,
): string[] {
  const set = new Set(values);
  if (on) set.add(value);
  else set.delete(value);
  return [...set];
}

/** Enable/disable a named subresource. New entries default to dry_run=true. */
function setSubresourceEnabled(
  list: SalesforceConfiguredSubresource[],
  name: string,
  on: boolean,
): SalesforceConfiguredSubresource[] {
  if (on) {
    if (list.some((s) => s.subresource === name)) return list;
    return [...list, { subresource: name, dry_run: true }];
  }
  return list.filter((s) => s.subresource !== name);
}

function setSubresourceDryRun(
  list: SalesforceConfiguredSubresource[],
  name: string,
  dryRun: boolean,
): SalesforceConfiguredSubresource[] {
  return list.map((s) =>
    s.subresource === name ? { ...s, dry_run: dryRun } : s,
  );
}

function parseFieldNameList(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

function objectAssigneeInput(
  primary: string,
  secondariesRaw: string,
): InteractionObjectAssigneeFieldInput | undefined {
  const name = primary.trim();
  if (name === "") return undefined;
  const secondaries = parseFieldNameList(secondariesRaw);
  const input: InteractionObjectAssigneeFieldInput = {
    primary_assignee_field_name: name,
  };
  if (secondaries.length > 0) {
    input.secondary_assignees = secondaries.map((field_name) => ({
      field_name,
    }));
  }
  return input;
}

type Status =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

type ImportMode = "incremental" | "full";

type ImportState =
  | { kind: "idle" }
  | { kind: "running"; mode: ImportMode; resources: string[] }
  | { kind: "done"; mode: ImportMode; resources: string[] }
  | { kind: "error"; message: string };

type ExportState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; dryRun: boolean; workflowId: string }
  | { kind: "error"; message: string };

type EnableInteractionAssigneesState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "done"; workflowId?: string; alreadyRunning?: boolean }
  | { kind: "error"; message: string };

/**
 * Read/update the whole Salesforce config: the connection triple plus the
 * import/export settings. PUT is read-modify-write server-side, so a partial
 * save preserves untouched fields. The client secret is write-only — we only
 * ever learn whether one is stored (`client_secret_set`).
 */
export function ConfigEditor({
  subscriberId,
  onSaved,
}: {
  subscriberId: string;
  onSaved: () => void;
}) {
  const [authUrl, setAuthUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [secretSet, setSecretSet] = useState(false);
  const [username, setUsername] = useState("");
  const [certificate, setCertificate] = useState<string | null>(null);
  const [certificateError, setCertificateError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [importState, setImportState] = useState<ImportState>({ kind: "idle" });
  const [exportState, setExportState] = useState<ExportState>({ kind: "idle" });
  /** Batch export dry_run; API defaults to true. */
  const [exportDryRun, setExportDryRun] = useState(true);
  /** Read-only watermark from GET; used as incremental lookback_date. */
  const [lastImportStartedAt, setLastImportStartedAt] = useState<string | null>(
    null,
  );
  /** Empty = all configured resources for this run. */
  const [importResources, setImportResources] = useState<string[]>([]);
  const [enableIaState, setEnableIaState] =
    useState<EnableInteractionAssigneesState>({ kind: "idle" });
  const [iaIncludeTask, setIaIncludeTask] = useState(true);
  const [iaIncludeEvent, setIaIncludeEvent] = useState(true);
  const [iaTaskPrimary, setIaTaskPrimary] = useState("OwnerId");
  const [iaEventPrimary, setIaEventPrimary] = useState("OwnerId");
  const [iaTaskSecondaries, setIaTaskSecondaries] = useState("");
  const [iaEventSecondaries, setIaEventSecondaries] = useState("");

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: "loading" });
    setClientSecret("");
    setLastImportStartedAt(null);
    setImportResources([]);
    setImportState({ kind: "idle" });
    setEnableIaState({ kind: "idle" });
    setIaIncludeTask(true);
    setIaIncludeEvent(true);
    setIaTaskPrimary("OwnerId");
    setIaEventPrimary("OwnerId");
    setIaTaskSecondaries("");
    setIaEventSecondaries("");
    getSalesforceConfig(subscriberId)
      .then((cfg) => {
        if (cancelled) return;
        setAuthUrl(cfg.auth_url ?? "");
        setClientId(cfg.client_id ?? "");
        setSecretSet(Boolean(cfg.client_secret_set));
        setUsername(cfg.username ?? "");
        setLastImportStartedAt(cfg.last_import_started_at ?? null);
        setSettings({
          export_to_sf_enabled: cfg.export_to_sf_enabled ?? false,
          resources_to_import: cfg.resources_to_import ?? [],
          resources_to_export: cfg.resources_to_export ?? [],
          resources_to_delete: cfg.resources_to_delete ?? [],
          resource_configuration: normalizeResourceConfiguration(
            cfg.resource_configuration,
          ),
          import_contact_social_links: cfg.import_contact_social_links ?? false,
          import_affiliation_social_links:
            cfg.import_affiliation_social_links ?? false,
          import_relationship_social_links:
            cfg.import_relationship_social_links ?? false,
          import_interval_in_minutes:
            cfg.import_interval_in_minutes == null
              ? ""
              : String(cfg.import_interval_in_minutes),
        });
        setStatus({ kind: "ready" });
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = nothing stored yet; start from a blank "set up connection" form.
        if (err instanceof ApiRequestError && err.status === 404) {
          setSecretSet(false);
          setLastImportStartedAt(null);
          setSettings({
            export_to_sf_enabled: false,
            resources_to_import: [],
            resources_to_export: [],
            resources_to_delete: [],
            resource_configuration: [],
            import_contact_social_links: false,
            import_affiliation_social_links: false,
            import_relationship_social_links: false,
            import_interval_in_minutes: "",
          });
          setStatus({ kind: "ready" });
        } else {
          setStatus({
            kind: "error",
            message:
              err instanceof ApiRequestError
                ? err.message
                : "Failed to load config.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [subscriberId]);

  function touch() {
    if (status.kind === "saved") setStatus({ kind: "ready" });
  }
  function setSetting(key: string, value: unknown) {
    setSettings((s) => ({ ...s, [key]: value }));
    touch();
  }

  const intervalRaw = String(settings.import_interval_in_minutes ?? "").trim();
  const intervalValid = intervalRaw === "" || /^\d+$/.test(intervalRaw);

  const canSave =
    authUrl.trim() !== "" &&
    clientId.trim() !== "" &&
    (clientSecret.trim() !== "" || secretSet || username.trim() !== "") &&
    intervalValid &&
    status.kind !== "saving" &&
    status.kind !== "loading";

  async function save() {
    if (!canSave) return;
    setStatus({ kind: "saving" });
    const input: SalesforceConfigInput = {
      auth_url: authUrl.trim(),
      client_id: clientId.trim(),
      export_to_sf_enabled: Boolean(settings.export_to_sf_enabled),
      resources_to_import: (settings.resources_to_import as string[]) ?? [],
      resources_to_export: (settings.resources_to_export as string[]) ?? [],
      resources_to_delete: (settings.resources_to_delete as string[]) ?? [],
      resource_configuration: normalizeResourceConfiguration(
        settings.resource_configuration,
      ),
      import_contact_social_links: Boolean(
        settings.import_contact_social_links,
      ),
      import_affiliation_social_links: Boolean(
        settings.import_affiliation_social_links,
      ),
      import_relationship_social_links: Boolean(
        settings.import_relationship_social_links,
      ),
    };
    const secret = clientSecret.trim();
    if (secret) input.client_secret = secret;
    input.username = username.trim() || null;
    if (intervalRaw !== "")
      input.import_interval_in_minutes = Number(intervalRaw);

    try {
      await putSalesforceConfig(subscriberId, input);
      if (secret) setSecretSet(true);
      setClientSecret("");
      setStatus({ kind: "saved" });
      onSaved();
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to save config.",
      });
    }
  }

  async function runImport(mode: ImportMode) {
    const resources = importResources.length > 0 ? importResources : undefined;
    if (mode === "full") {
      const ok = window.confirm(
        resources
          ? `This re-syncs ${resources.join(", ")} from scratch. Other resources are skipped. Continue?`
          : "This re-syncs every Salesforce record from scratch and can take a " +
              "long time. Continue?",
      );
      if (!ok) return;
    } else if (!lastImportStartedAt) {
      setImportState({
        kind: "error",
        message:
          "No last_import_started_at on this config — run a full reimport first, or wait for a scheduled import to set the watermark.",
      });
      return;
    }
    setImportState({ kind: "running", mode, resources: importResources });
    try {
      await triggerImport(subscriberId, {
        lookbackDate: mode === "full" ? "1900-01-01" : lastImportStartedAt,
        resources,
      });
      setImportState({ kind: "done", mode, resources: importResources });
    } catch (err) {
      setImportState({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to trigger import.",
      });
    }
  }

  async function runBatchExport() {
    if (!exportDryRun) {
      const ok = window.confirm(
        "This will write constituent assignee fields to Salesforce. Continue?",
      );
      if (!ok) return;
    }
    setExportState({ kind: "running" });
    try {
      const result = await triggerBatchExport(subscriberId, {
        resources: [CONSTITUENT_ASSIGNEES_EXPORT_RESOURCE],
        dryRun: exportDryRun,
      });
      setExportState({
        kind: "done",
        dryRun: exportDryRun,
        workflowId: result.workflow_id,
      });
    } catch (err) {
      setExportState({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to trigger batch export.",
      });
    }
  }

  async function refreshResourceConfiguration() {
    const cfg = await getSalesforceConfig(subscriberId);
    setSettings((s) => ({
      ...s,
      resource_configuration: normalizeResourceConfiguration(
        cfg.resource_configuration,
      ),
    }));
    setLastImportStartedAt(cfg.last_import_started_at ?? null);
  }

  async function runEnableInteractionAssignees() {
    const body: EnableInteractionAssigneeImportInput = {};
    if (iaIncludeTask) {
      const task = objectAssigneeInput(iaTaskPrimary, iaTaskSecondaries);
      if (!task) {
        setEnableIaState({
          kind: "error",
          message: "Task primary assignee field name is required.",
        });
        return;
      }
      body.task = task;
    }
    if (iaIncludeEvent) {
      const event = objectAssigneeInput(iaEventPrimary, iaEventSecondaries);
      if (!event) {
        setEnableIaState({
          kind: "error",
          message: "Event primary assignee field name is required.",
        });
        return;
      }
      body.event = event;
    }
    if (body.task == null && body.event == null) {
      setEnableIaState({
        kind: "error",
        message: "Select at least one of Task or Event.",
      });
      return;
    }
    const ok = window.confirm(
      "This writes interaction assignee mappings, enables the assignees subresource (dry run), and starts a Salesforce import. Continue?",
    );
    if (!ok) return;

    setEnableIaState({ kind: "running" });
    try {
      const result = await enableInteractionAssigneeImport(subscriberId, body);
      await refreshResourceConfiguration();
      setEnableIaState({
        kind: "done",
        workflowId: result.workflow_id,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 409) {
        try {
          await refreshResourceConfiguration();
        } catch {
          /* still surface the 409 */
        }
        setEnableIaState({
          kind: "done",
          alreadyRunning: true,
        });
        onSaved();
        return;
      }
      setEnableIaState({
        kind: "error",
        message:
          err instanceof ApiRequestError
            ? err.message
            : "Failed to enable interaction assignee import.",
      });
    }
  }

  const importing = importState.kind === "running";
  const exporting = exportState.kind === "running";

  if (status.kind === "loading") {
    return <div className="placeholder">Loading config…</div>;
  }

  function renderSetting(def: ConfigFieldDef) {
    const value = settings[def.key];
    if (def.kind === "bool") {
      return (
        <label key={def.key} className="field">
          <span className="check">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => setSetting(def.key, e.target.checked)}
            />
            {def.label}
          </span>
          <span className="hint">{def.help}</span>
        </label>
      );
    }
    if (def.kind === "int") {
      const raw = String(value ?? "");
      const bad = raw.trim() !== "" && !/^\d+$/.test(raw.trim());
      return (
        <label key={def.key} className="field">
          <span className="field-label">{def.label}</span>
          <input
            className={`num-input${bad ? " invalid" : ""}`}
            inputMode="numeric"
            placeholder={def.placeholder}
            value={raw}
            onChange={(e) => setSetting(def.key, e.target.value)}
          />
          <span className={`hint${bad ? " error" : ""}`}>
            {bad ? "Must be a whole number of minutes." : def.help}
          </span>
        </label>
      );
    }
    // enum-multi
    {
      const selected = new Set((value as string[]) ?? []);
      return (
        <div key={def.key} className="field">
          <span className="field-label">{def.label}</span>
          <span className="hint">{def.help}</span>
          <div className="chip-grid">
            {def.options?.map((opt) => (
              <label key={opt.value} className="chip-check" title={opt.help}>
                <input
                  type="checkbox"
                  checked={selected.has(opt.value)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(opt.value);
                    else next.delete(opt.value);
                    setSetting(def.key, [...next]);
                  }}
                />
                {opt.label}
                <code className="chip-code">{opt.value}</code>
              </label>
            ))}
          </div>
        </div>
      );
    }
  }

  function renderSubresourceList(
    list: SalesforceConfiguredSubresource[],
    field: "subresources_to_import" | "subresources_to_export",
    subresources: ResourceConfigurationSectionDef["subresources"],
    updateEntry: (patch: Partial<SalesforceResourceConfiguration>) => void,
  ) {
    return (
      <div className="subresource-list">
        {subresources.map((opt) => {
          const configured = list.find((s) => s.subresource === opt.value);
          const enabled = configured != null;
          return (
            <div key={`${field}-${opt.value}`} className="subresource-block">
              <label className="chip-check" title={opt.help}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) =>
                    updateEntry({
                      [field]: setSubresourceEnabled(
                        list,
                        opt.value,
                        e.target.checked,
                      ),
                    })
                  }
                />
                {opt.label}
                <code className="chip-code">{opt.value}</code>
              </label>
              {enabled && (
                <label
                  className="chip-check chip-check-nested"
                  title={SUBRESOURCE_DRY_RUN.help}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(configured?.dry_run)}
                    onChange={(e) =>
                      updateEntry({
                        [field]: setSubresourceDryRun(
                          list,
                          opt.value,
                          e.target.checked,
                        ),
                      })
                    }
                  />
                  {SUBRESOURCE_DRY_RUN.label}
                  <code className="chip-code">{SUBRESOURCE_DRY_RUN.value}</code>
                </label>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderResourceConfigurationSection(
    configs: SalesforceResourceConfiguration[],
    section: ResourceConfigurationSectionDef,
  ) {
    const entry = getResourceEntry(configs, section.resourceName);
    const subImport = entry.subresources_to_import ?? [];
    const subExport = entry.subresources_to_export ?? [];
    const flags = new Set(entry.flags ?? []);

    function updateEntry(patch: Partial<SalesforceResourceConfiguration>) {
      setSetting(
        "resource_configuration",
        withResourceEntry(configs, section.resourceName, {
          ...entry,
          ...patch,
        }),
      );
    }

    return (
      <div key={section.resourceName} className="field">
        <span className="field-label">{section.label}</span>
        <span className="hint">{section.help}</span>

        <span className="field-label" style={{ marginTop: "0.75rem" }}>
          Subresources to import
        </span>
        {renderSubresourceList(
          subImport,
          "subresources_to_import",
          section.subresources,
          updateEntry,
        )}

        {section.supportsExport && (
          <>
            <span className="field-label" style={{ marginTop: "0.75rem" }}>
              Subresources to export
            </span>
            {renderSubresourceList(
              subExport,
              "subresources_to_export",
              section.subresources,
              updateEntry,
            )}
          </>
        )}

        {section.flags.length > 0 && (
          <>
            <span className="field-label" style={{ marginTop: "0.75rem" }}>
              Flags
            </span>
            <div className="chip-grid">
              {section.flags.map((opt) => (
                <label
                  key={`flag-${section.resourceName}-${opt.value}`}
                  className="chip-check"
                  title={opt.help}
                >
                  <input
                    type="checkbox"
                    checked={flags.has(opt.value)}
                    onChange={(e) =>
                      updateEntry({
                        flags: toggleListValue(
                          [...flags],
                          opt.value,
                          e.target.checked,
                        ),
                      })
                    }
                  />
                  {opt.label}
                  <code className="chip-code">{opt.value}</code>
                </label>
              ))}
            </div>
          </>
        )}

        {section.resourceName === INTERACTIONS_RESOURCE_NAME &&
          renderInteractionAssigneeEnable()}
      </div>
    );
  }

  function renderInteractionAssigneeEnable() {
    const enabling = enableIaState.kind === "running";
    return (
      <>
        <span className="field-label" style={{ marginTop: "0.75rem" }}>
          Enable interaction assignee import
        </span>
        <span className="hint">
          Calls <code>POST …/salesforce/interaction-assignee-import/</code>.
          Resolves Task/Event User lookups, writes interaction mappings, enables{" "}
          <code>assignees</code> under interactions{" "}
          <code>subresources_to_import</code> (dry run), and starts an
          incremental import. Checking Assignees above only changes local config
          until you Save — use this to run the real enable path.
        </span>
        <label className="chip-check" style={{ marginTop: "0.5rem" }}>
          <input
            type="checkbox"
            checked={iaIncludeTask}
            disabled={enabling}
            onChange={(e) => {
              setIaIncludeTask(e.target.checked);
              setEnableIaState({ kind: "idle" });
            }}
          />
          Task
        </label>
        {iaIncludeTask && (
          <>
            <label className="field">
              <span className="field-label">Task primary field API name</span>
              <input
                className="text-input"
                spellCheck={false}
                autoComplete="off"
                value={iaTaskPrimary}
                disabled={enabling}
                onChange={(e) => {
                  setIaTaskPrimary(e.target.value);
                  setEnableIaState({ kind: "idle" });
                }}
              />
            </label>
            <label className="field">
              <span className="field-label">
                Task secondary field API names
              </span>
              <input
                className="text-input"
                spellCheck={false}
                autoComplete="off"
                placeholder="optional, comma-separated"
                value={iaTaskSecondaries}
                disabled={enabling}
                onChange={(e) => {
                  setIaTaskSecondaries(e.target.value);
                  setEnableIaState({ kind: "idle" });
                }}
              />
            </label>
          </>
        )}
        <label className="chip-check">
          <input
            type="checkbox"
            checked={iaIncludeEvent}
            disabled={enabling}
            onChange={(e) => {
              setIaIncludeEvent(e.target.checked);
              setEnableIaState({ kind: "idle" });
            }}
          />
          Event
        </label>
        {iaIncludeEvent && (
          <>
            <label className="field">
              <span className="field-label">Event primary field API name</span>
              <input
                className="text-input"
                spellCheck={false}
                autoComplete="off"
                value={iaEventPrimary}
                disabled={enabling}
                onChange={(e) => {
                  setIaEventPrimary(e.target.value);
                  setEnableIaState({ kind: "idle" });
                }}
              />
            </label>
            <label className="field">
              <span className="field-label">
                Event secondary field API names
              </span>
              <input
                className="text-input"
                spellCheck={false}
                autoComplete="off"
                placeholder="optional, comma-separated"
                value={iaEventSecondaries}
                disabled={enabling}
                onChange={(e) => {
                  setIaEventSecondaries(e.target.value);
                  setEnableIaState({ kind: "idle" });
                }}
              />
            </label>
          </>
        )}
        <div className="toolbar">
          <button
            type="button"
            className="btn"
            onClick={() => void runEnableInteractionAssignees()}
            disabled={enabling}
          >
            {enabling ? "Enabling…" : "Enable interaction assignee import"}
          </button>
          {enableIaState.kind === "done" && (
            <span className="hint ok-text">
              {enableIaState.alreadyRunning
                ? "Enabled, but an import is already running."
                : enableIaState.workflowId
                  ? `Enabled and import started (${enableIaState.workflowId}).`
                  : "Enabled."}
            </span>
          )}
        </div>
        {enableIaState.kind === "error" && (
          <div className="banner error">{enableIaState.message}</div>
        )}
      </>
    );
  }

  function renderResourceConfiguration() {
    const configs = normalizeResourceConfiguration(
      settings.resource_configuration,
    );
    return (
      <>
        {RESOURCE_CONFIGURATION_SECTIONS.map((section) =>
          renderResourceConfigurationSection(configs, section),
        )}
      </>
    );
  }

  return (
    <section className="panel">
      <div className="form form-wide">
        <h3 className="section-head">Connection</h3>
        {CONNECTION_FIELDS.map((def) => {
          if (def.kind === "secret") {
            return (
              <label key={def.key} className="field">
                <span className="field-label">{def.label}</span>
                <div className="secret-row">
                  <input
                    className="text-input grow"
                    type={showSecret ? "text" : "password"}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder={
                      secretSet ? "•••••••• (stored — leave blank to keep)" : ""
                    }
                    value={clientSecret}
                    onChange={(e) => {
                      setClientSecret(e.target.value);
                      touch();
                    }}
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowSecret((s) => !s)}
                  >
                    {showSecret ? "Hide" : "Show"}
                  </button>
                </div>
                <span className="hint">
                  {def.help}
                  {secretSet ? " A secret is currently stored." : ""}
                </span>
              </label>
            );
          }
          const [value, setter] = {
            auth_url: [authUrl, setAuthUrl],
            client_id: [clientId, setClientId],
            username: [username, setUsername],
          }[def.key] as [string, (v: string) => void];
          return (
            <label key={def.key} className="field">
              <span className="field-label">{def.label}</span>
              <input
                className="text-input"
                spellCheck={false}
                autoComplete="off"
                placeholder={def.placeholder}
                value={value}
                onChange={(e) => {
                  setter(e.target.value);
                  touch();
                }}
              />
              <span className="hint">{def.help}</span>
            </label>
          );
        })}

        <div className="toolbar">
          <button
            type="button"
            className="btn"
            onClick={async () => {
              setCertificateError(null);
              try {
                setCertificate(await getSalesforceJwtCertificate(subscriberId));
              } catch (err) {
                setCertificateError(
                  err instanceof ApiRequestError ? err.message : String(err),
                );
              }
            }}
          >
            Get JWT certificate
          </button>
          <span className="hint">
            Makes the subscriber's key pair on first use. Instil keeps the
            private key.
          </span>
        </div>
        {certificateError && (
          <div className="banner error">{certificateError}</div>
        )}
        {certificate && <pre className="cmd">{certificate}</pre>}

        <h3 className="section-head">Import / export settings</h3>
        {SETTINGS_FIELDS.map(renderSetting)}
        {renderResourceConfiguration()}

        <div className="toolbar">
          <button
            type="button"
            className="btn primary"
            onClick={save}
            disabled={!canSave}
          >
            {status.kind === "saving" ? "Saving…" : "Save config"}
          </button>
          {status.kind === "saved" && (
            <span className="hint ok-text">Saved.</span>
          )}
        </div>

        {status.kind === "error" && (
          <div className="banner error">{status.message}</div>
        )}

        <p className="hint footnote">
          Partial saves are safe: the server merges these fields into the stored
          record, so fields you don't touch are preserved. The client secret is
          write-only — leave it blank to keep the stored one.
        </p>

        <h3 className="section-head">Data import</h3>
        <p className="hint">
          Trigger a Salesforce import job. Incremental passes{" "}
          <code>last_import_started_at</code> as <code>lookback_date</code>.
          A full reimport re-syncs from scratch. Selecting resources restricts the
          run (intersected with configured <code>resources_to_import</code>) and
          does not advance the import watermark. Leave the list empty to import
          every configured resource.
        </p>
        <p className="hint">
          Last sync:{" "}
          {lastImportStartedAt ? (
            <code>{lastImportStartedAt}</code>
          ) : (
            <em>not set</em>
          )}
        </p>
        <label className="field">
          <span className="field-label">Resources for this run</span>
          <select
            className="select multi"
            multiple
            size={IMPORTABLE_RESOURCES.length}
            value={importResources}
            disabled={importing}
            onChange={(e) => {
              setImportResources(
                Array.from(e.target.selectedOptions, (opt) => opt.value),
              );
              if (importState.kind === "done" || importState.kind === "error") {
                setImportState({ kind: "idle" });
              }
            }}
          >
            {IMPORTABLE_RESOURCES.map((opt) => (
              <option key={opt.value} value={opt.value} title={opt.help}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="hint">
            Hold Cmd (Mac) or Ctrl (Windows) to select more than one.
          </span>
        </label>
        <div className="toolbar">
          <button
            type="button"
            className="btn"
            onClick={() => runImport("incremental")}
            disabled={importing || !lastImportStartedAt}
            title={
              lastImportStartedAt
                ? importResources.length > 0
                  ? `lookback_date=${lastImportStartedAt} resources=[${importResources.join(", ")}]`
                  : `lookback_date=${lastImportStartedAt}`
                : "Requires last_import_started_at on the Salesforce config"
            }
          >
            {importState.kind === "running" &&
            importState.mode === "incremental"
              ? "Running…"
              : "Run incremental import"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => runImport("full")}
            disabled={importing}
            title={
              importResources.length > 0
                ? `lookback_date=1900-01-01 resources=[${importResources.join(", ")}]`
                : "lookback_date=1900-01-01"
            }
          >
            {importState.kind === "running" && importState.mode === "full"
              ? "Running…"
              : importResources.length > 0
                ? "Full reimport (selected resources)"
                : "Full reimport (re-syncs everything)"}
          </button>
          {importState.kind === "done" && (
            <span className="hint ok-text">
              {importState.mode === "full"
                ? "Full reimport"
                : "Incremental import"}
              {importState.resources.length > 0
                ? ` of ${importState.resources.join(", ")}`
                : ""}{" "}
              triggered.
            </span>
          )}
        </div>
        {importState.kind === "error" && (
          <div className="banner error">{importState.message}</div>
        )}

        <h3 className="section-head">Batch export</h3>
        <p className="hint">
          Trigger <code>BatchExportToSalesforceWorkflow</code> for{" "}
          <code>{CONSTITUENT_ASSIGNEES_EXPORT_RESOURCE}</code>. Dry-run prepares
          and records rows without writing to Salesforce.
        </p>
        <label className="field">
          <span className="check">
            <input
              type="checkbox"
              checked={exportDryRun}
              onChange={(e) => {
                setExportDryRun(e.target.checked);
                if (
                  exportState.kind === "done" ||
                  exportState.kind === "error"
                ) {
                  setExportState({ kind: "idle" });
                }
              }}
              disabled={exporting}
            />
            Dry run
          </span>
          <span className="hint">
            Leave on to prepare/record only. Uncheck to write assignee fields to
            Salesforce.
          </span>
        </label>
        <div className="toolbar">
          <button
            type="button"
            className="btn"
            onClick={runBatchExport}
            disabled={exporting}
            title={
              exportDryRun
                ? `resources=[${CONSTITUENT_ASSIGNEES_EXPORT_RESOURCE}] dry_run=true`
                : `resources=[${CONSTITUENT_ASSIGNEES_EXPORT_RESOURCE}] dry_run=false`
            }
          >
            {exporting
              ? "Starting…"
              : exportDryRun
                ? "Run assignee export (dry run)"
                : "Run assignee export (apply)"}
          </button>
          {exportState.kind === "done" && (
            <span className="hint ok-text">
              {exportState.dryRun ? "Dry-run" : "Apply"} export triggered (
              <code>{exportState.workflowId}</code>).
            </span>
          )}
        </div>
        {exportState.kind === "error" && (
          <div className="banner error">{exportState.message}</div>
        )}
      </div>
    </section>
  );
}
